// DoorReport.jsx - WCAG AA Accessible Version
//
// SETUP INSTRUCTIONS:
// 1. Get a Google Places API key from: https://console.cloud.google.com/
//    - Enable "Places API" and "Maps JavaScript API"
// 2. Replace 'YOUR_GOOGLE_PLACES_API_KEY' on line ~1346 with your actual API key
// 3. For production, restrict your API key to your domain
//
import { useState, useEffect, useRef, useCallback } from "react";
import api from "./services/api";
import AuthModal from "./components/AuthModal";
import AdminDashboard from "./components/AdminDashboard";
import AccountPage from "./components/AccountPage";

// ==================== GOOGLE PLACES AUTOCOMPLETE ====================
// Hook to load Google Places API
function useGooglePlacesScript(apiKey) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Check if already loaded
    if (window.google?.maps?.places) {
      setLoaded(true);
      return;
    }

    // Don't load if no API key
    if (!apiKey || apiKey === 'YOUR_GOOGLE_PLACES_API_KEY') {
      console.warn('Google Places API key not configured. Address autocomplete will not work.');
      return;
    }

    // Create script element
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setLoaded(true);
    
    document.head.appendChild(script);

    // Add custom styles for autocomplete dropdown
    const style = document.createElement('style');
    style.textContent = `
      .pac-container {
        font-family: Georgia, serif !important;
        border-radius: 12px !important;
        border: 1px solid #e0e0e0 !important;
        box-shadow: 0 8px 40px rgba(0,0,0,0.15) !important;
        margin-top: 4px !important;
      }
      .pac-item {
        padding: 10px 14px !important;
        font-size: 14px !important;
        border-top: 1px solid #f0f0f0 !important;
        cursor: pointer !important;
      }
      .pac-item:first-child {
        border-top: none !important;
      }
      .pac-item:hover {
        background-color: #fff5f0 !important;
      }
      .pac-item-selected {
        background-color: #fff5f0 !important;
      }
      .pac-icon {
        margin-top: 2px !important;
      }
      .pac-item-query {
        font-size: 14px !important;
        color: #1a1a2e !important;
        font-weight: 600 !important;
      }
      .pac-matched {
        color: #E8622A !important;
        font-weight: 700 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      // Cleanup if needed
    };
  }, [apiKey]);

  return loaded;
}

// Hook to setup autocomplete on an input
function useAddressAutocomplete(inputRef, onPlaceSelected, isLoaded, trigger) {
  useEffect(() => {
    if (!isLoaded || !inputRef.current || !window.google?.maps?.places) return;

    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ["address"],
      componentRestrictions: { country: "us" }, // Restrict to US addresses
      fields: ["address_components", "formatted_address", "geometry"],
    });

    const listener = autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      
      if (!place.address_components) {
        return;
      }

      // Parse address components
      const addressComponents = {};
      place.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes("street_number")) {
          addressComponents.streetNumber = component.long_name;
        }
        if (types.includes("route")) {
          addressComponents.street = component.long_name;
        }
        if (types.includes("locality")) {
          addressComponents.city = component.long_name;
        }
        if (types.includes("administrative_area_level_1")) {
          addressComponents.state = component.short_name;
        }
        if (types.includes("postal_code")) {
          addressComponents.zipCode = component.long_name;
        }
      });

      // Format the address
      const formattedAddress = place.formatted_address || "";
      
      onPlaceSelected({
        formattedAddress,
        addressComponents,
        geometry: place.geometry,
      });
    });

    return () => {
      if (window.google?.maps?.event) {
        window.google.maps.event.removeListener(listener);
      }
    };
  }, [isLoaded, inputRef, onPlaceSelected, trigger]);
}

// ==================== RESPONSIVE HOOK ====================
function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return width;
}

// ==================== CONSTANTS ====================
const GOOGLE_API_KEY = 'AIzaSyBOg3YD-RIuWNt8M2AdN4XwS4-4VTvHOXE';

const categoryLabels = {
  maintenance: "Maintenance",
  pest: "Pest-Free",
  safety: "Safety",
  waterMold: "Water/Mold",
  noise: "Noise Level",
  landlord: "Property Management",
};

// eslint-disable-next-line no-unused-vars
const ISSUE_TAGS = [
  "AC Issues", "Heating Issues", "Plumbing", "Roof Leaks", "Mold",
  "Pest Issues", "Flooding", "Slow Repairs", "Good Property Mgmt", "Bad Property Mgmt",
  "Quiet Street", "Noisy Neighbors", "Security Concerns", "Pet Friendly",
];

// ==================== ACCESSIBLE STAR RATING ====================
function StarRating({ value, size = 16, interactive = false, onChange, label = "Rating" }) {
  const [hover, setHover] = useState(null);
  const display = hover ?? value;

  // For screen readers: announce the rating
  const srText = interactive
    ? `${label}: ${value || 0} out of 5 stars. Use arrow keys or click to change.`
    : `${label}: ${value || 0} out of 5 stars`;

  const handleKeyDown = (e, starValue) => {
    if (!interactive || !onChange) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onChange(starValue);
    } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = Math.min(5, (value || 0) + 1);
      onChange(next);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault();
      const prev = Math.max(1, (value || 0) - 1);
      onChange(prev);
    }
  };

  return (
    <div
      role={interactive ? "radiogroup" : "img"}
      aria-label={srText}
    >
      {/* Screen reader text */}
      <span
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0,0,0,0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {srText}
      </span>
      <span style={{ display: "inline-flex", gap: 2 }} aria-hidden={!interactive}>
        {[1, 2, 3, 4, 5].map((s) => (
          <span
            key={s}
            role={interactive ? "radio" : undefined}
            aria-checked={interactive ? s === value : undefined}
            aria-label={interactive ? `${s} star${s > 1 ? "s" : ""}` : undefined}
            tabIndex={interactive ? (s === (value || 1) ? 0 : -1) : undefined}
            style={{
              fontSize: size,
              color: s <= display ? "#E8622A" : "#999",
              transition: "color 0.1s",
              cursor: interactive ? "pointer" : "default",
              lineHeight: 1,
            }}
            onMouseEnter={() => interactive && setHover(s)}
            onMouseLeave={() => interactive && setHover(null)}
            onClick={() => interactive && onChange && onChange(s)}
            onKeyDown={(e) => handleKeyDown(e, s)}
          >
            ★
          </span>
        ))}
      </span>
    </div>
  );
}

// ==================== RATING BAR ====================
function RatingBar({ label, value }) {
  const percentage = value ? (value / 5) * 100 : 0;
  const color = value >= 4 ? "#2e7d32" : value >= 2.5 ? "#E8622A" : "#c62828";

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span
          id={`rating-label-${label.replace(/\s+/g, "-").toLowerCase()}`}
          style={{ fontSize: 13, color: "#444", fontFamily: "Georgia, serif" }}
        >
          {label}
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e" }}>
          {value?.toFixed(1) || "N/A"}
        </span>
      </div>
      <div
        role="meter"
        aria-label={`${label} rating`}
        aria-valuenow={value || 0}
        aria-valuemin={0}
        aria-valuemax={5}
        aria-labelledby={`rating-label-${label.replace(/\s+/g, "-").toLowerCase()}`}
        style={{ height: 6, background: "#e0e0e0", borderRadius: 4, overflow: "hidden" }}
      >
        <div
          style={{
            height: "100%",
            width: `${percentage}%`,
            background: color,
            borderRadius: 4,
            transition: "width 0.6s ease",
          }}
        />
      </div>
    </div>
  );
}

// ==================== REVIEW CARD ====================
function ReviewCard({ review }) {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };

  const formatTenancy = () => {
    if (review.reviewType === "tour") {
      if (!review.tourDate) return null;
      const date = new Date(review.tourDate).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
      return `Toured: ${date}`;
    }
    if (!review.tenancy?.moveInDate) return null;
    const start = new Date(review.tenancy.moveInDate).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    });
    const end = review.tenancy.isCurrentTenant
      ? "Present"
      : review.tenancy.moveOutDate
        ? new Date(review.tenancy.moveOutDate).toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
          })
        : "Present";
    return `${start} – ${end}`;
  };

  return (
    <article
      aria-label={`Review: ${review.title}, rated ${review.overallRating} out of 5`}
      style={{
        background: "#fff",
        border: "1px solid #e0e0e0",
        borderRadius: 12,
        padding: "20px 24px",
        marginBottom: 16,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 8,
        }}
      >
        <div>
          <StarRating value={review.overallRating} size={15} label={review.title} />
          <h3
            style={{
              fontWeight: 700,
              fontSize: 15,
              marginTop: 4,
              margin: "4px 0 0",
              color: "#1a1a2e",
              fontFamily: "Georgia, serif",
            }}
          >
            {review.title}
          </h3>
        </div>
        <div style={{ textAlign: "right" }}>
          <time
            dateTime={review.createdAt}
            style={{ fontSize: 12, color: "#666" }}
          >
            {formatDate(review.createdAt)}
          </time>
          <div
            style={{
              fontSize: 11,
              color: review.reviewType === "tour" ? "#1565c0" : "#2e7d32",
              marginTop: 2,
              fontWeight: 600,
            }}
            aria-label={review.reviewType === "tour" ? "Tour review" : "Verified tenant"}
          >
            {review.reviewType === "tour" ? "👀 Tour Review" : "✓ Verified Tenant"}
          </div>
        </div>
      </div>
      {formatTenancy() && (
        <p style={{ fontSize: 13, color: "#666", margin: "0 0 10px" }}>
          {review.reviewType === "tour" ? formatTenancy() : `Tenancy: ${formatTenancy()}`}
        </p>
      )}
      <p style={{ fontSize: 14, color: "#444", lineHeight: 1.65, margin: "0 0 12px" }}>
        {review.reviewText}
      </p>
    </article>
  );
}

// ==================== FOCUS TRAP HOOK ====================
function useFocusTrap(ref, isActive) {
  useEffect(() => {
    if (!isActive || !ref.current) return;

    const element = ref.current;
    const focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

    const handleKeyDown = (e) => {
      if (e.key !== "Tab") return;

      const focusableElements = element.querySelectorAll(focusableSelector);
      const firstEl = focusableElements[0];
      const lastEl = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };

    element.addEventListener("keydown", handleKeyDown);
    // Focus the first focusable element
    const firstFocusable = element.querySelector(focusableSelector);
    if (firstFocusable) firstFocusable.focus();

    return () => element.removeEventListener("keydown", handleKeyDown);
  }, [isActive, ref]);
}

// ==================== WRITE REVIEW MODAL ====================
function WriteReviewModal({ propertyId, address, onClose, onSubmit }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const modalRef = useRef(null);
  const previousFocus = useRef(null);

  const [form, setForm] = useState({
    reviewType: null, // 'tenant' | 'tour'
    overall: 0,
    title: "",
    body: "",
    tenancyStart: "",
    tenancyEnd: "",
    tourDate: "",
    isCurrent: false,
    wouldRecommend: null,
    ratings: { maintenance: 0, pest: 0, safety: 0, waterMold: 0, noise: 0, landlord: 0 },
  });

  const setCatRating = (cat, val) => {
    setForm((f) => ({ ...f, ratings: { ...f.ratings, [cat]: val } }));
  };

  // Focus management
  useFocusTrap(modalRef, true);

  useEffect(() => {
    previousFocus.current = document.activeElement;
    return () => {
      if (previousFocus.current) previousFocus.current.focus();
    };
  }, []);

  // Escape key closes modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const handleSubmit = async () => {
    if (!api.isAuthenticated()) {
      setError("Please sign in to submit a review");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // Strip category ratings that were left at 0 (not rated) — backend rejects 0
      const categoryRatings = Object.fromEntries(
        Object.entries(form.ratings).filter(([, v]) => v > 0)
      );

      const reviewData = {
        propertyId,
        title: form.title,
        reviewText: form.body,
        overallRating: form.overall,
        reviewType: form.reviewType,
        // Backend always requires moveInDate + isCurrentTenant; for tour reviews
        // we satisfy that constraint by using the tour date as the move-in date.
        moveInDate: form.reviewType === "tenant" ? form.tenancyStart : form.tourDate,
        moveOutDate: form.reviewType === "tenant" && !form.isCurrent ? form.tenancyEnd : null,
        isCurrentTenant: form.reviewType === "tenant" ? form.isCurrent : false,
        ...(form.reviewType === "tour" && { tourDate: form.tourDate }),
        wouldRecommend: form.wouldRecommend,
        ...(Object.keys(categoryRatings).length > 0 && { categoryRatings }),
      };

      await api.submitReview(reviewData);
      setSuccess(true);
      setTimeout(() => {
        onSubmit?.();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.message || "Failed to submit review");
    } finally {
      setLoading(false);
    }
  };

  const inputStyles = {
    width: "100%",
    padding: "10px 12px",
    border: "2px solid #ccc",
    borderRadius: 8,
    fontSize: 14,
    fontFamily: "Georgia, serif",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
    outline: "none",
  };

  const inputFocusHandler = (e) => {
    e.target.style.borderColor = "#E8622A";
    e.target.style.boxShadow = "0 0 0 3px rgba(232,98,42,0.2)";
  };

  const inputBlurHandler = (e) => {
    e.target.style.borderColor = "#ccc";
    e.target.style.boxShadow = "none";
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="review-modal-title"
      ref={modalRef}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
        backdropFilter: "blur(2px)",
        padding: 20,
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: "28px",
          width: "100%",
          maxWidth: 520,
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 20,
          }}
        >
          <div>
            <h2
              id="review-modal-title"
              style={{
                fontSize: 18,
                margin: 0,
                fontFamily: "Georgia, serif",
                fontWeight: 700,
                color: "#1a1a2e",
              }}
            >
              Write a Review
            </h2>
            {address && (
              <p style={{ fontFamily: "Georgia, serif", fontWeight: 700, color: "#1a1a2e", marginTop: 2, fontSize: 14 }}>
                {address}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close review form"
            style={{
              background: "none",
              border: "2px solid transparent",
              fontSize: 24,
              cursor: "pointer",
              color: "#666",
              padding: "4px 8px",
              borderRadius: 4,
              lineHeight: 1,
            }}
            onFocus={(e) => (e.target.style.borderColor = "#E8622A")}
            onBlur={(e) => (e.target.style.borderColor = "transparent")}
          >
            ×
          </button>
        </div>

        {/* Progress indicator — only shown once a review type is chosen */}
        {form.reviewType && (
          <div role="progressbar" aria-valuenow={step} aria-valuemin={1} aria-valuemax={3} aria-label={`Step ${step} of 3`}>
            <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
              {[
                form.reviewType === "tour" ? "Rating & Tour Info" : "Rating & Tenancy",
                "Categories",
                "Write Review",
              ].map((label, i) => (
                <div key={i} style={{ flex: 1, textAlign: "center" }}>
                  <div
                    style={{
                      height: 4,
                      borderRadius: 2,
                      background: i + 1 <= step ? "#E8622A" : "#e0e0e0",
                      transition: "background 0.3s",
                      marginBottom: 4,
                    }}
                  />
                  <span style={{ fontSize: 11, color: i + 1 <= step ? "#E8622A" : "#999" }}>
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Live region for errors and success */}
        <div aria-live="polite" aria-atomic="true">
          {error && (
            <div
              role="alert"
              style={{
                background: "#fef2f2",
                color: "#991b1b",
                padding: "10px 14px",
                borderRadius: 8,
                fontSize: 13,
                marginBottom: 16,
                border: "1px solid #fecaca",
              }}
            >
              {error}
            </div>
          )}

          {success && (
            <div
              role="status"
              style={{
                background: "#f0fdf4",
                color: "#166534",
                padding: "20px",
                borderRadius: 8,
                textAlign: "center",
                fontSize: 15,
                fontWeight: 600,
                border: "1px solid #bbf7d0",
              }}
            >
              Review submitted successfully! Thank you for helping other renters.
            </div>
          )}
        </div>

        {/* Review type selector */}
        {!success && !form.reviewType && (
          <div>
            <p style={{ fontSize: 14, color: "#444", marginBottom: 16, marginTop: 0 }}>
              How do you know this property?
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <button
                onClick={() => setForm((f) => ({ ...f, reviewType: "tenant" }))}
                style={{
                  padding: "16px 20px",
                  background: "#fff",
                  border: "2px solid #e0e0e0",
                  borderRadius: 12,
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "border-color 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#E8622A")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e0e0e0")}
              >
                <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1a2e", marginBottom: 4 }}>
                  🏠 I lived here
                </div>
                <div style={{ fontSize: 13, color: "#666" }}>
                  I was a tenant at this property
                </div>
              </button>
              <button
                onClick={() => setForm((f) => ({ ...f, reviewType: "tour" }))}
                style={{
                  padding: "16px 20px",
                  background: "#fff",
                  border: "2px solid #e0e0e0",
                  borderRadius: 12,
                  cursor: "pointer",
                  textAlign: "left",
                  transition: "border-color 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#E8622A")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e0e0e0")}
              >
                <div style={{ fontWeight: 700, fontSize: 15, color: "#1a1a2e", marginBottom: 4 }}>
                  👀 I toured this property
                </div>
                <div style={{ fontSize: 13, color: "#666" }}>
                  I visited but did not live here
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Overall Rating & Tenancy / Tour Info */}
        {!success && form.reviewType && step === 1 && (
          <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
            <legend
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0,0,0,0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
            >
              Overall rating and tenancy dates
            </legend>

            {/* Back to type selection */}
            <button
              onClick={() => setForm((f) => ({ ...f, reviewType: null }))}
              style={{
                background: "none",
                border: "none",
                color: "#E8622A",
                fontSize: 12,
                cursor: "pointer",
                padding: 0,
                marginBottom: 16,
                textDecoration: "underline",
              }}
            >
              ← Change review type
            </button>

            <div style={{ marginBottom: 20 }}>
              <label
                id="overall-rating-label"
                style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 8 }}
              >
                Overall Rating <span aria-hidden="true">*</span>
                <span style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}>
                  (required)
                </span>
              </label>
              <StarRating
                value={form.overall}
                size={28}
                interactive
                onChange={(v) => setForm({ ...form, overall: v })}
                label="Overall rating"
              />
            </div>

            {form.reviewType === "tenant" ? (
              <>
                <div style={{ marginBottom: 20 }}>
                  <label
                    htmlFor="move-in-date"
                    style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 8 }}
                  >
                    Move-in Date <span aria-hidden="true">*</span>
                    <span style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}>
                      (required)
                    </span>
                  </label>
                  <input
                    id="move-in-date"
                    type="date"
                    value={form.tenancyStart}
                    onChange={(e) => setForm({ ...form, tenancyStart: e.target.value })}
                    style={inputStyles}
                    onFocus={inputFocusHandler}
                    onBlur={inputBlurHandler}
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={form.isCurrent}
                      onChange={(e) => setForm({ ...form, isCurrent: e.target.checked, tenancyEnd: "" })}
                      style={{ width: 18, height: 18, accentColor: "#E8622A" }}
                    />
                    I currently live here
                  </label>
                </div>

                {!form.isCurrent && (
                  <div style={{ marginBottom: 20 }}>
                    <label
                      htmlFor="move-out-date"
                      style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 8 }}
                    >
                      Move-out Date
                    </label>
                    <input
                      id="move-out-date"
                      type="date"
                      value={form.tenancyEnd}
                      onChange={(e) => setForm({ ...form, tenancyEnd: e.target.value })}
                      style={inputStyles}
                      onFocus={inputFocusHandler}
                      onBlur={inputBlurHandler}
                    />
                  </div>
                )}
              </>
            ) : (
              <div style={{ marginBottom: 20 }}>
                <label
                  htmlFor="tour-date"
                  style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 8 }}
                >
                  Tour Date <span aria-hidden="true">*</span>
                  <span style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}>
                    (required)
                  </span>
                </label>
                <input
                  id="tour-date"
                  type="date"
                  value={form.tourDate}
                  onChange={(e) => setForm({ ...form, tourDate: e.target.value })}
                  style={inputStyles}
                  onFocus={inputFocusHandler}
                  onBlur={inputBlurHandler}
                />
              </div>
            )}

            {(() => {
              const canProceed = form.reviewType === "tenant"
                ? !!(form.overall && form.tenancyStart)
                : !!(form.overall && form.tourDate);
              return (
                <button
                  onClick={() => setStep(2)}
                  disabled={!canProceed}
                  aria-disabled={!canProceed}
                  style={{
                    width: "100%",
                    padding: "12px",
                    background: canProceed ? "#E8622A" : "#ccc",
                    color: "#fff",
                    border: "2px solid transparent",
                    borderRadius: 10,
                    fontWeight: 700,
                    fontSize: 14,
                    cursor: canProceed ? "pointer" : "not-allowed",
                  }}
                >
                  Next: Category Ratings
                </button>
              );
            })()}
          </fieldset>
        )}

        {/* Step 2: Category Ratings */}
        {!success && step === 2 && (
          <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
            <legend
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0,0,0,0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
            >
              Category ratings
            </legend>

            <p style={{ fontSize: 13, color: "#666", marginBottom: 16, marginTop: 0 }}>
              Rate specific aspects (optional but recommended)
            </p>

            {Object.entries(categoryLabels).map(([key, label]) => (
              <div key={key} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#1a1a2e" }}>
                  {label}
                </div>
                <StarRating
                  value={form.ratings[key]}
                  size={22}
                  interactive
                  onChange={(v) => setCatRating(key, v)}
                  label={label}
                />
              </div>
            ))}

            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={() => setStep(1)}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#fff",
                  color: "#E8622A",
                  border: "2px solid #E8622A",
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#E8622A",
                  color: "#fff",
                  border: "2px solid transparent",
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                Next: Write Review
              </button>
            </div>
          </fieldset>
        )}

        {/* Step 3: Written Review */}
        {!success && step === 3 && (
          <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
            <legend
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0,0,0,0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
            >
              Write your review
            </legend>

            <div style={{ marginBottom: 20 }}>
              <label
                htmlFor="review-title"
                style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 8 }}
              >
                Review Title <span aria-hidden="true">*</span>
                <span style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}>
                  (required)
                </span>
              </label>
              <input
                id="review-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Sum up your experience"
                maxLength={200}
                aria-describedby="title-hint"
                aria-invalid={form.title.length > 0 && form.title.length < 10}
                style={inputStyles}
                onFocus={inputFocusHandler}
                onBlur={inputBlurHandler}
              />
              <small 
                id="title-hint" 
                style={{ 
                  fontSize: 12, 
                  color: form.title.length > 0 && form.title.length < 10 ? '#E8622A' : '#666', 
                  marginTop: 4, 
                  display: "block" 
                }}
              >
                {form.title.length}/200 characters (minimum 10 required)
              </small>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label
                htmlFor="review-body"
                style={{ display: "block", fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 8 }}
              >
                Your Experience <span aria-hidden="true">*</span>
                <span style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}>
                  (required, minimum 50 characters)
                </span>
              </label>
              <textarea
                id="review-body"
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="Share details about maintenance, property management responsiveness, neighbors, issues..."
                rows={6}
                aria-describedby="body-char-count"
                style={{ ...inputStyles, resize: "vertical" }}
                onFocus={inputFocusHandler}
                onBlur={inputBlurHandler}
              />
              <span
                id="body-char-count"
                style={{ fontSize: 12, color: form.body.length >= 50 ? "#2e7d32" : "#666", marginTop: 4, display: "block" }}
                aria-live="polite"
              >
                {form.body.length} / 50 characters minimum
              </span>
            </div>

            <div style={{ marginBottom: 20 }}>
              <fieldset style={{ border: "none", padding: 0, margin: 0 }}>
                <legend style={{ fontSize: 13, fontWeight: 700, color: "#1a1a2e", marginBottom: 8, padding: 0 }}>
                  Would you recommend this property?
                </legend>
                <div style={{ display: "flex", gap: 12 }} role="radiogroup">
                  <button
                    role="radio"
                    aria-checked={form.wouldRecommend === true}
                    onClick={() => setForm({ ...form, wouldRecommend: true })}
                    style={{
                      flex: 1,
                      padding: "10px",
                      background: form.wouldRecommend === true ? "#2e7d32" : "#f5f5f5",
                      color: form.wouldRecommend === true ? "#fff" : "#444",
                      border: "2px solid",
                      borderColor: form.wouldRecommend === true ? "#2e7d32" : "#ddd",
                      borderRadius: 8,
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    👍 Yes
                  </button>
                  <button
                    role="radio"
                    aria-checked={form.wouldRecommend === false}
                    onClick={() => setForm({ ...form, wouldRecommend: false })}
                    style={{
                      flex: 1,
                      padding: "10px",
                      background: form.wouldRecommend === false ? "#c62828" : "#f5f5f5",
                      color: form.wouldRecommend === false ? "#fff" : "#444",
                      border: "2px solid",
                      borderColor: form.wouldRecommend === false ? "#c62828" : "#ddd",
                      borderRadius: 8,
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: "pointer",
                    }}
                  >
                    👎 No
                  </button>
                </div>
              </fieldset>
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={() => setStep(2)}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#fff",
                  color: "#E8622A",
                  border: "2px solid #E8622A",
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || !form.title || form.body.length < 50}
                aria-disabled={loading || !form.title || form.body.length < 50}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: !loading && form.title && form.body.length >= 50 ? "#E8622A" : "#ccc",
                  color: "#fff",
                  border: "2px solid transparent",
                  borderRadius: 10,
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: !loading && form.title && form.body.length >= 50 ? "pointer" : "not-allowed",
                }}
              >
                {loading ? "Submitting..." : "Submit Review"}
              </button>
            </div>
          </fieldset>
        )}
      </div>
    </div>
  );
}

// ==================== PROPERTY PAGE ====================
function PropertyPage({ propertyId, onBack, isAuthenticated: initialAuth, onSignOut }) {
  const [property, setProperty] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [pendingReview, setPendingReview] = useState(false);
  const [error, setError] = useState(null);
  const [streetViewUrl, setStreetViewUrl] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(initialAuth);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favLoading, setFavLoading] = useState(false);

  // After sign-in, open the review modal if the user clicked "Write a Review" first
  useEffect(() => {
    if (isAuthenticated && pendingReview) {
      setPendingReview(false);
      setShowReviewModal(true);
    }
  }, [isAuthenticated, pendingReview]);

  // Record history and check favorite status when authenticated
  useEffect(() => {
    if (isAuthenticated && propertyId) {
      api.recordHistory(propertyId).catch(() => {});
      api.checkFavorite(propertyId).then((d) => setIsFavorited(d.favorited)).catch(() => {});
    }
  }, [isAuthenticated, propertyId]);

  const toggleFavorite = async () => {
    if (!isAuthenticated) { setShowAuthModal(true); return; }
    setFavLoading(true);
    try {
      if (isFavorited) {
        await api.removeFavorite(propertyId);
        setIsFavorited(false);
      } else {
        await api.addFavorite(propertyId);
        setIsFavorited(true);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setFavLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const propData = await api.getPropertyDetails(propertyId);
        setProperty(propData.property);

        const reviewData = await api.getReviews(propertyId);
        setReviews(reviewData.reviews || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [propertyId]);

  useEffect(() => {
    if (!property) return;
    const addr = property.address?.line1 && property.address?.city && property.address?.state && property.address?.zipCode
      ? `${property.address.line1}, ${property.address.city}, ${property.address.state} ${property.address.zipCode}`
      : null;
    if (!addr) return;

    const imageUrl = `https://maps.googleapis.com/maps/api/streetview?size=640x320&location=${encodeURIComponent(addr)}&key=${GOOGLE_API_KEY}`;

    // Already cached — no Google API call needed
    if (property.streetViewAvailable === true) {
      setStreetViewUrl(imageUrl);
      return;
    }
    if (property.streetViewAvailable === false) {
      return;
    }

    // Not yet checked — call metadata API and save result
    const checkStreetView = async () => {
      try {
        const res = await fetch(
          `https://maps.googleapis.com/maps/api/streetview/metadata?location=${encodeURIComponent(addr)}&key=${GOOGLE_API_KEY}`
        );
        const data = await res.json();
        const available = data.status === 'OK';
        api.updateStreetViewAvailability(propertyId, available).catch(() => {});
        if (available) {
          setStreetViewUrl(imageUrl);
        }
      } catch {
        // No photo available — silently skip
      }
    };

    checkStreetView();
  }, [property, propertyId]);

  if (loading) {
    return (
      <div
        role="status"
        aria-label="Loading property details"
        style={{
          minHeight: "100vh",
          background: "#f5f5f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <p style={{ fontSize: 16, color: "#666" }}>Loading property details...</p>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          background: "#f5f5f0",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
        }}
      >
        <p style={{ fontSize: 16, color: "#991b1b", marginBottom: 16 }}>
          {error || "Property not found."}
        </p>
        <button
          onClick={onBack}
          style={{
            padding: "10px 24px",
            background: "#E8622A",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Back to Search
        </button>
      </div>
    );
  }

  const address = property.address?.line1 && property.address?.city && property.address?.state && property.address?.zipCode
    ? `${property.address.line1}, ${property.address.city}, ${property.address.state} ${property.address.zipCode}`
    : null;

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f0" }}>
      {/* Skip link */}
      <a
        href="#property-content"
        style={{
          position: "absolute",
          left: -9999,
          top: 0,
          padding: "8px 16px",
          background: "#E8622A",
          color: "#fff",
          zIndex: 200,
          fontWeight: 600,
        }}
        onFocus={(e) => (e.target.style.left = "0")}
        onBlur={(e) => (e.target.style.left = "-9999px")}
      >
        Skip to property content
      </a>

      {/* Header */}
      <header style={{ background: "#1a1a2e", padding: "16px 24px" }}>
        <nav aria-label="Property page navigation" style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button
            onClick={onBack}
            aria-label="Back to search"
            style={{
              background: "none",
              border: "2px solid transparent",
              color: "#fff",
              cursor: "pointer",
              fontSize: 18,
              padding: "4px 8px",
              borderRadius: 4,
            }}
            onFocus={(e) => (e.target.style.borderColor = "#E8622A")}
            onBlur={(e) => (e.target.style.borderColor = "transparent")}
          >
            ← Back
          </button>
          <span style={{ color: "#fff", fontFamily: "Georgia, serif", fontSize: 18, fontWeight: 700, flex: 1 }}>
            Door<span style={{ color: "#E8622A" }}>Report</span>
          </span>
          <button
            onClick={toggleFavorite}
            disabled={favLoading}
            aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 22, color: isFavorited ? "#E8622A" : "rgba(255,255,255,0.4)", padding: "4px 8px", lineHeight: 1 }}
          >
            {isFavorited ? "♥" : "♡"}
          </button>
          {isAuthenticated ? (
            <button
              onClick={onSignOut}
              style={{
                background: "none",
                border: "2px solid transparent",
                color: "rgba(255,255,255,0.7)",
                fontSize: 13,
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: 4,
              }}
              onFocus={(e) => (e.target.style.borderColor = "#E8622A")}
              onBlur={(e) => (e.target.style.borderColor = "transparent")}
            >
              Sign Out
            </button>
          ) : (
            <button
              onClick={() => setShowAuthModal(true)}
              style={{
                background: "none",
                border: "2px solid transparent",
                color: "rgba(255,255,255,0.7)",
                fontSize: 13,
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: 4,
              }}
              onFocus={(e) => (e.target.style.borderColor = "#E8622A")}
              onBlur={(e) => (e.target.style.borderColor = "transparent")}
            >
              Sign In
            </button>
          )}
        </nav>
      </header>

      {/* Main content */}
      <main id="property-content" style={{ maxWidth: 640, margin: "0 auto", padding: "24px 16px" }}>
        <h1
          style={{
            fontSize: 22,
            fontFamily: "Georgia, serif",
            fontWeight: 700,
            color: "#1a1a2e",
            margin: "0 0 12px",
          }}
        >
          {address}
        </h1>

        {property.details?.propertyType && (
          <p style={{ fontSize: 13, color: "#666", margin: "0 0 20px" }}>
            {property.details.propertyType} · {property.details.bedrooms || "?"} bed · {property.details.bathrooms || "?"} bath
          </p>
        )}

        {streetViewUrl && (
          <div style={{ borderRadius: 12, overflow: "hidden", marginBottom: 20 }}>
            <img
              src={streetViewUrl}
              alt={`Street view of ${address}`}
              style={{ width: "100%", display: "block", height: 220, objectFit: "cover" }}
            />
          </div>
        )}

        {/* Write Review Button */}
        <button
          onClick={() => {
            if (!isAuthenticated) {
              setPendingReview(true);
              setShowAuthModal(true);
            } else {
              setShowReviewModal(true);
            }
          }}
          style={{
            width: "100%",
            padding: "14px",
            background: "#E8622A",
            color: "#fff",
            border: "none",
            borderRadius: 10,
            fontWeight: 700,
            fontSize: 15,
            cursor: "pointer",
            marginTop: 16,
            marginBottom: 24,
          }}
        >
          Write a Review
        </button>

        {/* Overall Rating Summary */}
        <section aria-labelledby="rating-summary-heading" style={{ marginBottom: 24 }}>
          <h2
            id="rating-summary-heading"
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: "hidden",
              clip: "rect(0,0,0,0)",
              whiteSpace: "nowrap",
              border: 0,
            }}
          >
            Rating Summary
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
            <div
              style={{
                fontSize: 48,
                fontWeight: 800,
                color: "#1a1a2e",
                fontFamily: "Georgia, serif",
                lineHeight: 1,
              }}
              aria-hidden="true"
            >
              {property.stats?.averageRating?.toFixed(1) || "N/A"}
            </div>
            <div>
              <StarRating
                value={Math.round(property.stats?.averageRating || 0)}
                size={20}
                label="Average property rating"
              />
              <p style={{ fontSize: 13, color: "#666", marginTop: 2, margin: "2px 0 0" }}>
                {property.stats?.totalReviews || 0} review{(property.stats?.totalReviews || 0) !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
        </section>

        {/* Category Ratings */}
        {property.categoryAverages && (
          <section
            aria-labelledby="category-ratings-heading"
            style={{
              background: "#fff",
              borderRadius: 14,
              padding: "20px 28px",
              marginBottom: 20,
              boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
            }}
          >
            <h2
              id="category-ratings-heading"
              style={{
                margin: "0 0 16px",
                fontSize: 15,
                fontWeight: 700,
                color: "#1a1a2e",
                fontFamily: "Georgia, serif",
              }}
            >
              Category Ratings
            </h2>
            {Object.entries(categoryLabels).map(([key, label]) => (
              <RatingBar key={key} label={label} value={property.categoryAverages?.[key]} />
            ))}
          </section>
        )}

        {/* Reviews */}
        <section aria-labelledby="reviews-heading">
          <h2
            id="reviews-heading"
            style={{
              margin: "0 0 14px",
              fontSize: 15,
              fontWeight: 700,
              color: "#1a1a2e",
              fontFamily: "Georgia, serif",
            }}
          >
            Reviews ({reviews.length})
          </h2>

          {reviews.length === 0 ? (
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                padding: "40px 24px",
                textAlign: "center",
                color: "#666",
              }}
            >
              <p>No reviews yet. Be the first to review this property!</p>
            </div>
          ) : (
            reviews.map((r) => <ReviewCard key={r.id} review={r} />)
          )}
        </section>
      </main>

      {/* Review Modal */}
      {showReviewModal && (
        <WriteReviewModal
          propertyId={propertyId}
          address={address}
          onClose={() => setShowReviewModal(false)}
          onSubmit={() => {
            api.getReviews(propertyId).then((data) => setReviews(data.reviews || []));
          }}
        />
      )}

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onSuccess={() => {
            setIsAuthenticated(true);
            setShowAuthModal(false);
          }}
        />
      )}
    </div>
  );
}

// ==================== ABOUT PAGE ====================
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const DEFAULT_ABOUT = {
  about_headline: "Renters deserve to know what they're getting into.",
  about_mission: "DoorReport exists because signing a lease is one of the biggest decisions you make — and most people do it with almost no real information about the property or the people managing it.",
  about_why_p1: "The rental market has a transparency problem. Large property management companies manage thousands of houses, and a bad experience at one address rarely makes it back to the next tenant. Reviews on Google or Yelp are tied to the company — not the house. You can rent from the same company in two different neighborhoods and have completely different experiences.",
  about_why_p2: "DoorReport ties reviews to the actual address. Every review you read is from someone who lived there or toured it — not a customer service interaction with a call center. We built this to give renters the kind of honest, specific information they actually need before they sign.",
  about_jacksonville: "We're launching in Jacksonville, where the single-family rental market is dominated by large institutional landlords. Tens of thousands of homes are managed by companies like Invitation Homes, Progress Residential, and American Homes 4 Rent. DoorReport gives Jacksonville renters a place to share what those experiences are actually like — address by address.",
  about_cta: "Know a rental address worth reviewing?",
};

function AboutPage({ onBack, onSearch }) {
  const [content, setContent] = useState(DEFAULT_ABOUT);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/content`)
      .then((r) => r.json())
      .then((data) => setContent((prev) => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f0" }}>
      <header style={{ background: "#1a1a2e", padding: "16px 24px" }}>
        <nav style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <button onClick={onBack} style={{ background: "none", border: "none", color: "#fff", cursor: "pointer", fontSize: 16 }}>← Back</button>
          <span style={{ fontFamily: "Georgia, serif", fontSize: 20, fontWeight: 700, color: "#fff" }}>
            Door<span style={{ color: "#E8622A" }}>Report</span>
          </span>
        </nav>
      </header>

      <main style={{ maxWidth: 680, margin: "0 auto", padding: "48px 24px 80px" }}>
        <p style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: "#E8622A", fontWeight: 700, margin: "0 0 16px" }}>Our Mission</p>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: "clamp(28px, 5vw, 42px)", color: "#1a1a2e", margin: "0 0 20px", lineHeight: 1.2 }}>
          {content.about_headline}
        </h1>
        <p style={{ fontSize: 16, color: "#555", lineHeight: 1.8, margin: "0 0 48px" }}>{content.about_mission}</p>

        <div style={{ width: 48, height: 3, background: "#E8622A", borderRadius: 2, marginBottom: 48 }} />

        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a1a2e", margin: "0 0 14px" }}>Why we built this</h2>
        <p style={{ fontSize: 15, color: "#555", lineHeight: 1.8, margin: "0 0 16px" }}>{content.about_why_p1}</p>
        <p style={{ fontSize: 15, color: "#555", lineHeight: 1.8, margin: "0 0 48px" }}>{content.about_why_p2}</p>

        <h2 style={{ fontFamily: "Georgia, serif", fontSize: 22, color: "#1a1a2e", margin: "0 0 24px" }}>How it works</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 48 }}>
          {[
            ["Search an address", "Type in any rental address and see reviews from people who actually lived there or toured the property."],
            ["Read real tenant reviews", "Reviews cover maintenance, property management responsiveness, pest control, safety, noise, and more — from people who experienced it firsthand."],
            ["Leave your own review", "Lived somewhere? Toured a place that didn't match the listing? Your review helps the next person avoid a bad situation or feel confident about a good one."],
          ].map(([title, body], i) => (
            <div key={i} style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#E8622A", color: "#fff", fontFamily: "Georgia, serif", fontWeight: 700, fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</div>
              <div>
                <p style={{ fontFamily: "Georgia, serif", fontWeight: 700, fontSize: 16, color: "#1a1a2e", margin: "0 0 4px" }}>{title}</p>
                <p style={{ fontSize: 14, color: "#666", margin: 0, lineHeight: 1.7 }}>{body}</p>
              </div>
            </div>
          ))}
        </div>

        <div style={{ background: "#1a1a2e", borderRadius: 14, padding: "28px 32px", marginBottom: 48 }}>
          <p style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: "#E8622A", fontWeight: 700, margin: "0 0 10px" }}>Where we're starting</p>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: 20, color: "#fff", margin: "0 0 12px" }}>Jacksonville, FL</h2>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", lineHeight: 1.8, margin: 0 }}>{content.about_jacksonville}</p>
        </div>

        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: 15, color: "#555", marginBottom: 20 }}>{content.about_cta}</p>
          <button onClick={onSearch} style={{ padding: "14px 36px", background: "#E8622A", color: "#fff", border: "none", borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: "Georgia, serif" }}>
            Search an Address
          </button>
        </div>
      </main>
    </div>
  );
}

// ==================== MAIN APP ====================
export default function DoorReport() {
  const [query, setQuery] = useState("");
  const [page, setPage] = useState("home");
  const [currentPropertyId, setCurrentPropertyId] = useState(null);
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [noStreetNumber, setNoStreetNumber] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(api.isAuthenticated());
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [platformStats, setPlatformStats] = useState(null);
  const searchInputRef = useRef(null);
  const isMobile = useWindowWidth() < 600;

  // Load platform stats and admin status once on mount
  useEffect(() => {
    api.getStats().then(setPlatformStats).catch(() => {});
    if (api.isAuthenticated()) {
      api.getCurrentUser()
        .then(({ user }) => { if (user.is_admin) setIsAdmin(true); })
        .catch(() => {});
    }
  }, []);

  // Load Google Places API
  // IMPORTANT: Replace 'YOUR_GOOGLE_PLACES_API_KEY' with your actual API key
  const placesLoaded = useGooglePlacesScript(GOOGLE_API_KEY);

  // Handle place selection from autocomplete
  const handlePlaceSelected = useCallback((placeData) => {
    setQuery(placeData.formattedAddress);
    setSelectedAddress(placeData);
    setNotFound(false);
  }, []);

  // Setup autocomplete
  useAddressAutocomplete(searchInputRef, handlePlaceSelected, placesLoaded, page);

  const handleSearch = async () => {
    if (!query.trim()) return;

    setSearching(true);
    setNotFound(false);
    setNoStreetNumber(false);

    try {
      let parsedAddress;
      if (selectedAddress?.addressComponents) {
        const ac = selectedAddress.addressComponents;
        if (!ac.streetNumber) {
          setNoStreetNumber(true);
          setSearching(false);
          return;
        }
        parsedAddress = {
          addressLine1: `${ac.streetNumber} ${ac.street || ''}`.trim(),
          city: ac.city,
          state: ac.state,
          zipCode: ac.zipCode,
        };
      } else {
        // Manual entry — require leading street number
        if (!/^\d+\s/.test(query.trim())) {
          setNoStreetNumber(true);
          setSearching(false);
          return;
        }
        parsedAddress = api.parseAddress(query);
      }

      const { property } = await api.getOrCreateProperty(parsedAddress);
      setCurrentPropertyId(property.id);
      setPage("property");
    } catch (error) {
      console.error("Search error:", error);
      setNotFound(true);
    } finally {
      setSearching(false);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleAuthSuccess = async () => {
    setIsAuthenticated(true);
    try {
      const { user } = await api.getCurrentUser();
      if (user.is_admin) {
        setIsAdmin(true);
        setPage("admin");
      }
    } catch (e) {
      // not admin or fetch failed, stay on current page
    }
  };

  const handleLogout = async () => {
    await api.logout();
    setIsAuthenticated(false);
    setIsAdmin(false);
    setPage("home");
  };

  const handleBackToHome = () => {
    setPage("home");
    setQuery("");
    setSelectedAddress(null);
    setNotFound(false);
    setNoStreetNumber(false);
  };

  if (page === "about") {
    return <AboutPage onBack={() => setPage("home")} onSearch={() => setPage("home")} />;
  }

  if (page === "account") {
    return (
      <AccountPage
        onBack={() => setPage("home")}
        onNavigateToProperty={(id) => {
          setCurrentPropertyId(id);
          setPage("property");
        }}
        onSignOut={handleLogout}
      />
    );
  }

  if (page === "admin" && isAdmin) {
    return (
      <AdminDashboard
        onBack={() => setPage("home")}
        onSignOut={handleLogout}
      />
    );
  }

  if (page === "property" && currentPropertyId) {
    return (
      <PropertyPage
        propertyId={currentPropertyId}
        onBack={handleBackToHome}
        isAuthenticated={isAuthenticated}
        onSignOut={handleLogout}
      />
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#1a1a2e", display: "flex", flexDirection: "column" }}>
      {/* Skip to content link */}
      <a
        href="#main-content"
        style={{
          position: "absolute",
          left: -9999,
          top: 0,
          padding: "8px 16px",
          background: "#E8622A",
          color: "#fff",
          zIndex: 200,
          fontWeight: 600,
          fontSize: 14,
        }}
        onFocus={(e) => (e.target.style.left = "0")}
        onBlur={(e) => (e.target.style.left = "-9999px")}
      >
        Skip to main content
      </a>

      {/* Navigation */}
      <header>
        <nav
          aria-label="Main navigation"
          style={{
            padding: isMobile ? "12px 16px" : "18px 32px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              setPage("home");
            }}
            style={{
              color: "#fff",
              fontFamily: "Georgia, serif",
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: -0.5,
              textDecoration: "none",
            }}
            aria-label="DoorReport home"
          >
            Door<span style={{ color: "#E8622A" }}>Report</span>
          </a>
          <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
            <button
              onClick={() => setPage("about")}
              style={{
                background: "none",
                border: "2px solid transparent",
                color: "rgba(255,255,255,0.7)",
                fontSize: 13,
                cursor: "pointer",
                padding: "4px 8px",
                borderRadius: 4,
              }}
              onFocus={(e) => (e.target.style.borderColor = "#E8622A")}
              onBlur={(e) => (e.target.style.borderColor = "transparent")}
            >
              About
            </button>
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => setPage("account")}
                  style={{
                    background: "none",
                    border: "2px solid transparent",
                    color: "rgba(255,255,255,0.7)",
                    fontSize: 13,
                    cursor: "pointer",
                    padding: "4px 8px",
                    borderRadius: 4,
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#E8622A")}
                  onBlur={(e) => (e.target.style.borderColor = "transparent")}
                >
                  Account
                </button>
                {isAdmin && (
                  <button
                    onClick={() => setPage("admin")}
                    style={{
                      background: "none",
                      border: "2px solid transparent",
                      color: "#E8622A",
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: "pointer",
                      padding: "4px 8px",
                      borderRadius: 4,
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#E8622A")}
                    onBlur={(e) => (e.target.style.borderColor = "transparent")}
                  >
                    Admin
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  style={{
                    background: "none",
                    border: "2px solid transparent",
                    color: "rgba(255,255,255,0.7)",
                    fontSize: 13,
                    cursor: "pointer",
                    padding: "4px 8px",
                    borderRadius: 4,
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#E8622A")}
                  onBlur={(e) => (e.target.style.borderColor = "transparent")}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <button
                onClick={() => setShowAuthModal(true)}
                style={{
                  background: "none",
                  border: "2px solid transparent",
                  color: "rgba(255,255,255,0.7)",
                  fontSize: 13,
                  cursor: "pointer",
                  padding: "4px 8px",
                  borderRadius: 4,
                }}
                onFocus={(e) => (e.target.style.borderColor = "#E8622A")}
                onBlur={(e) => (e.target.style.borderColor = "transparent")}
              >
                Sign In
              </button>
            )}
          </div>
        </nav>
      </header>

      {/* Main Content */}
      <main
        id="main-content"
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 20px 80px",
        }}
      >
        <p
          style={{
            fontSize: 11,
            letterSpacing: 3,
            textTransform: "uppercase",
            color: "#E8622A",
            fontWeight: 700,
            marginBottom: 16,
            margin: "0 0 16px",
          }}
        >
          Real Reviews · Real Addresses
        </p>
        <h1
          style={{
            fontFamily: "Georgia, serif",
            fontSize: "clamp(32px, 6vw, 56px)",
            color: "#fff",
            textAlign: "center",
            margin: "0 0 12px",
            fontWeight: 700,
            lineHeight: 1.15,
            maxWidth: 600,
          }}
        >
          Know what you're renting
          <br />
          <span style={{ color: "#E8622A" }}>before you sign.</span>
        </h1>
        <p
          style={{
            color: "rgba(255,255,255,0.65)",
            fontSize: 16,
            textAlign: "center",
            maxWidth: 420,
            marginBottom: 36,
            lineHeight: 1.6,
          }}
        >
          Search any rental address for real tenant reviews — not just the management company.
        </p>

        {/* Search */}
        <div
          role="search"
          aria-label="Search for a rental property"
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            width: "100%",
            maxWidth: 520,
            boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
            borderRadius: 14,
            overflow: "hidden",
          }}
        >
          <label htmlFor="property-search" style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}>
            Enter a rental property address
          </label>
          <input
            id="property-search"
            ref={searchInputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedAddress(null);
            }}
            onKeyDown={handleSearchKeyDown}
            placeholder="Start typing an address..."
            aria-describedby={notFound ? "search-error" : "search-hint"}
            aria-invalid={notFound}
            autoComplete="off"
            style={{
              flex: 1,
              padding: "16px 20px",
              border: "none",
              fontSize: 15,
              fontFamily: "Georgia, serif",
              outline: "none",
              background: "#fff",
            }}
          />
          <button
            onClick={handleSearch}
            disabled={searching || !query.trim()}
            aria-label="Search properties"
            style={{
              padding: "16px 28px",
              background: "#E8622A",
              color: "#fff",
              border: "none",
              fontWeight: 700,
              fontSize: 14,
              cursor: searching || !query.trim() ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {searching ? "Searching..." : "Search"}
          </button>
        </div>

        {/* Search feedback */}
        <div aria-live="polite" style={{ marginTop: 16, textAlign: "center" }}>
          {!searching && !notFound && !query && placesLoaded && (
            <p id="search-hint" style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>
              💡 Start typing to see address suggestions
            </p>
          )}
          {noStreetNumber && (
            <p id="search-error" role="alert" style={{ color: "#fca5a5", fontSize: 14 }}>
              Please include a street number (e.g., 123 Main St).
            </p>
          )}
          {notFound && !noStreetNumber && (
            <p id="search-error" role="alert" style={{ color: "#fca5a5", fontSize: 14 }}>
              Property not found. Please select a valid address from the suggestions.
            </p>
          )}
          {searching && (
            <p role="status" style={{ color: "rgba(255,255,255,0.6)", fontSize: 14 }}>
              Searching for property...
            </p>
          )}
        </div>

        {/* Stats */}
        <div
          style={{ display: "flex", gap: isMobile ? 24 : 40, marginTop: isMobile ? 36 : 56 }}
          aria-label="Platform statistics"
        >
          {[
            [platformStats ? platformStats.properties.toLocaleString() : "—", "Properties"],
            [platformStats ? platformStats.reviews.toLocaleString() : "—", "Reviews"],
            ["Jacksonville, FL", "Launching In"],
          ].map(([val, label]) => (
            <div key={label} style={{ textAlign: "center" }}>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: "#fff",
                  fontFamily: "Georgia, serif",
                }}
              >
                {val}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.5)",
                  marginTop: 2,
                  letterSpacing: 0.5,
                }}
              >
                {label}
              </div>
            </div>
          ))}
        </div>
      </main>

      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onSuccess={handleAuthSuccess}
        />
      )}
    </div>
  );
}
